/**
 * Banner — the single section/page-level notice strip (see DESIGN-SYSTEM.md "Banners").
 *
 * Replaces the old Alert component and every hand-rolled colored strip.
 * Variant fixes the colour semantics AND the aria role so they cannot drift:
 * error announces assertively (role="alert"); warning/info announce politely
 * (role="status"). A success is not a banner: it is a toast (Toast.tsx).
 *
 * Dismissal is the caller's business: passing onDismiss renders the X, but
 * whether that dismiss is ephemeral (local state) or acknowledged (persisted
 * via API) is decided — and documented — at the call site.
 *
 * Kept in lockstep with web/src/components/ui/Banner.tsx — same API, same
 * module class names, same declarations; only the icon source differs (admin
 * uses its inline-SVG shim, web uses lucide-react). The `icon` override is
 * the RMS's own, for the gear list's date notice.
 */
import type { ReactNode } from "react";
import { cx } from "@shared/cx";
import { AlertTriangleIcon, InfoIcon, XIcon } from "./icons";
import styles from "./Banner.module.css";

export type BannerVariant = "info" | "warning" | "error";

interface BannerProps {
  variant: BannerVariant;
  /** Bold first line. Optional — short banners can be body-only children. */
  title?: string;
  children?: ReactNode;
  /**
   * Custom leading icon node (e.g. CalendarClockIcon for date notices).
   * Defaults to the variant icon; pass false to render no icon.
   */
  icon?: ReactNode | false;
  /** Renders the X button. The caller owns what dismissal means/persists. */
  onDismiss?: () => void;
  /** Accessible label for the X button. */
  dismissLabel?: string;
  /** Disables the X while a persisted dismiss is in flight. */
  dismissing?: boolean;
  className?: string;
  "data-testid"?: string;
}

function defaultIcon(variant: BannerVariant, className: string): ReactNode {
  switch (variant) {
    case "info":
      return <InfoIcon className={className} />;
    case "warning":
    case "error":
      return <AlertTriangleIcon className={className} />;
  }
}

export function Banner({
  variant,
  title,
  children,
  icon,
  onDismiss,
  dismissLabel = "Dismiss",
  dismissing = false,
  className = "",
  "data-testid": testId,
}: BannerProps) {
  // The variant class carries the strip's tint AND the tones its icon, title
  // and body take, so a call site never passes a colour — the rule DESIGN-SYSTEM.md
  // states for Select's `badge` holds here for the same reason.
  const iconNode =
    icon === false ? null : (icon ?? defaultIcon(variant, styles.icon));

  return (
    <div
      role={variant === "error" ? "alert" : "status"}
      className={cx(styles.banner, styles[variant], className)}
      data-testid={testId}
    >
      <div className={styles.row}>
        {iconNode}
        <div className={styles.body}>
          {title && <p className={styles.title}>{title}</p>}
          {children}
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            disabled={dismissing}
            aria-label={dismissLabel}
            className={styles.dismiss}
          >
            <XIcon className={styles.dismissIcon} />
          </button>
        )}
      </div>
    </div>
  );
}
